import { useEffect, useState, useRef } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Features.css";

export function Features() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const featuresRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize AOS
  useEffect(() => {
    AOS.init({ duration: 1200 });
  }, []);

  // Mouse parallax effect
  const handleMouseMove = (e) => {
    if (featuresRef.current && !isMobile) {
      const rect = featuresRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Calculate normalized position (0 to 1)
      const normalizedX = x / rect.width;
      const normalizedY = y / rect.height;
      
      setMousePosition({ x: normalizedX, y: normalizedY });
    }
  };

  // Calculate parallax transforms for different elements
  const getParallaxStyle = (speed = 1, elementIndex = 0) => {
    if (isMobile) return {};
    
    const offsetX = (mousePosition.x - 0.5) * speed * 15;
    const offsetY = (mousePosition.y - 0.5) * speed * 15;
    
    // Add some variation based on element index
    const variation = Math.sin(elementIndex * 0.5) * 3;
    
    return {
      transform: `translate(${offsetX + variation}px, ${offsetY + variation}px)`,
      transition: 'transform 0.1s ease-out'
    };
  };

  // Calculate central model parallax - optimized for performance
  const getModelParallaxStyle = () => {
    if (isMobile) return {};
    
    const offsetX = (mousePosition.x - 0.5) * 15; // Reduced from 20
    const offsetY = (mousePosition.y - 0.5) * 15; // Reduced from 20
    const rotateX = (mousePosition.y - 0.5) * 5; // Reduced from 10
    const rotateY = (mousePosition.x - 0.5) * 5; // Reduced from 10
    
    return {
      transform: `translate(${offsetX}px, ${offsetY}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      transition: 'transform 0.3s ease-out' // Increased from 0.2s for smoother movement
    };
  };

  const features = [
    {
      id: 1,
      title: "تعلم تفاعلي متطور",
      description: "دروس تفاعلية مع محتوى مرئي وسمعي متطور، اختبارات تفاعلية، ومحاكيات عملية لضمان الفهم العميق",
      icon: "🎯",
      color: "#2196f3",
      details: [
        "محتوى مرئي وسمعي متطور",
        "اختبارات تفاعلية فورية",
        "محاكيات عملية للتدريب",
        "تقييم مستمر للأداء"
      ]
    },
    {
      id: 2,
      title: "نظام متابعة ذكي",
      description: "نظام متابعة ذكي لتطورك التعليمي مع تقارير مفصلة وإحصائيات دقيقة لقياس تقدمك",
      icon: "📊",
      color: "#4caf50",
      details: [
        "تقارير أداء مفصلة",
        "إحصائيات التقدم اليومية",
        "مؤشرات النجاح الشخصية",
        "توصيات مخصصة للتحسين"
      ]
    },
    {
      id: 3,
      title: "شهادات معتمدة دولياً",
      description: "شهادات معترف بها دولياً من مؤسسات تعليمية مرموقة تفتح لك أبواب الفرص العالمية",
      icon: "🏆",
      color: "#ff9800",
      details: [
        "شهادات معتمدة من جامعات عالمية",
        "اعتراف دولي بالشهادات",
        "شهادات تخصصية في مجالات مختلفة",
        "إمكانية التحويل للجامعات"
      ]
    },
    {
      id: 4,
      title: "دعم فني متخصص 24/7",
      description: "فريق دعم متخصص على مدار الساعة لمساعدتك في أي وقت تحتاج فيه للمساعدة",
      icon: "💬",
      color: "#9c27b0",
      details: [
        "دعم فني على مدار الساعة",
        "فريق متخصص في كل مجال",
        "رد فوري على الاستفسارات",
        "جلسات استشارية مجانية"
      ]
    },
    {
      id: 5,
      title: "مناهج محدثة باستمرار",
      description: "مناهج حديثة تتوافق مع متطلبات السوق الحالية وتحديثات مستمرة للمحتوى",
      icon: "📚",
      color: "#f44336",
      details: [
        "مناهج محدثة شهرياً",
        "توافق مع متطلبات السوق",
        "محتوى حديث ومتطور",
        "أحدث التقنيات والمنهجيات"
      ]
    },
    {
      id: 6,
      title: "مجتمع تعليمي نشط",
      description: "انضم لمجتمع من المتعلمين النشطين، شارك خبراتك وتعلم من الآخرين",
      icon: "👥",
      color: "#00bcd4",
      details: [
        "مجتمع تعليمي نشط",
        "منتديات نقاش متخصصة",
        "مشاريع تعاونية",
        "شبكة علاقات مهنية"
      ]
    },
    {
      id: 7,
      title: "تعلم مخصص لك",
      description: "خطة تعليمية مخصصة تناسب مستواك وأهدافك مع مرونة في الجدول الزمني",
      icon: "🎨",
      color: "#e91e63",
      details: [
        "خطة تعليمية شخصية",
        "جدول زمني مرن",
        "محتوى مخصص للمستوى",
        "أهداف تعليمية واضحة"
      ]
    },
    {
      id: 8,
      title: "تقنيات تعليمية متقدمة",
      description: "استخدام أحدث التقنيات التعليمية مثل الذكاء الاصطناعي والواقع المعزز",
      icon: "🤖",
      color: "#673ab7",
      details: [
        "ذكاء اصطناعي في التعليم",
        "واقع معزز للتدريب",
        "تقنيات تفاعلية متقدمة",
        "منصات تعليمية حديثة"
      ]
    }
  ];

  const handleFeatureHover = (featureId) => {
    setActiveFeature(featureId);
  };

  const handleFeatureLeave = () => {
    setActiveFeature(null);
  };

  return (
    <section className="features-section" ref={featuresRef} onMouseMove={handleMouseMove}>
      <div className="container">
        <div className="features-header" data-aos="fade-up">
          <h2 className="features-title">مميزات منصة علمني</h2>
          {/* وصف تسويقي قوي */}
          <p className="features-marketing-desc">
            منصة علمني هي بوابتك لعالم من التعلم الذكي والتطور الحقيقي! انضم لأكبر مجتمع تعليمي عربي، واستمتع بتجربة تعليمية تفاعلية، شهادات معتمدة، دعم فني متواصل، ومحتوى متجدد يواكب سوق العمل. مع علمني، مستقبلك يبدأ الآن!
          </p>
          <p className="features-subtitle">اكتشف ما يجعل منصتنا الأفضل لتعليمك</p>
          {/* Statistics Section */}
          <div className="features-stats" data-aos="fade-up" data-aos-delay="200">
            <div className="stat-item">
              <div className="stat-number">50,000+</div>
              <div className="stat-label">طالب نشط</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">دورة تعليمية</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">95%</div>
              <div className="stat-label">معدل رضا الطلاب</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">دعم فني متواصل</div>
            </div>
          </div>
        </div>

        {/* قسم ماركتنج إضافي */}
        <div className="features-marketing-highlights" data-aos="fade-up" data-aos-delay="250">
          <div className="marketing-highlight">
            <span className="highlight-icon">🚀</span>
            <div>
              <div className="highlight-title">الأسرع نموًا في العالم العربي</div>
              <div className="highlight-desc">منصة علمني تحقق أعلى معدلات النمو وعدد الطلاب الجدد شهريًا مقارنة بالمنصات المنافسة.</div>
            </div>
          </div>
          <div className="marketing-highlight">
            <span className="highlight-icon">🌍</span>
            <div>
              <div className="highlight-title">انتشار عالمي</div>
              <div className="highlight-desc">طلاب من أكثر من 20 دولة حول العالم يثقون في جودة محتوى علمني.</div>
            </div>
          </div>
          <div className="marketing-highlight">
            <span className="highlight-icon">💡</span>
            <div>
              <div className="highlight-title">تقنيات تعليمية متقدمة</div>
              <div className="highlight-desc">ذكاء اصطناعي، واقع معزز، وتفاعل مباشر مع أفضل الخبراء.</div>
            </div>
          </div>
          <div className="marketing-highlight">
            <span className="highlight-icon">⭐</span>
            <div>
              <div className="highlight-title">تقييمات ممتازة</div>
              <div className="highlight-desc">أكثر من 95% من الطلاب يوصون بالمنصة لأصدقائهم.</div>
            </div>
          </div>
        </div>

        <div className="features-container" style={{ marginTop: '200px' }}>
          {/* Central 3D Model */}
          <div className="central-model" style={getModelParallaxStyle()}>
            <div className="model-container">
              <div className="model-sphere">
                <div className="sphere-inner">
                  <div className="sphere-core">🎓</div>
                </div>
              </div>
              <div className="model-rings">
                <div className="ring ring-1"></div>
                <div className="ring ring-2"></div>
                <div className="ring ring-3"></div>
              </div>
            </div>
          </div>

          {/* 3D Grid Distribution */}
          <div className="features-grid">
            {features.map((feature, index) => {
              // توزيع شبكة ثلاثية الأبعاد مبتكر
              const layer = Math.floor(index / 3); // 3 طبقات
              const positionInLayer = index % 3;
              
              // زوايا مختلفة لكل طبقة
              const layerAngles = [0, 120, 240]; // 0°, 120°, 240°
              const baseAngle = (layerAngles[layer] + positionInLayer * 40) * (Math.PI / 180);
              
              // مسافات مختلفة لكل طبقة
              const layerRadii = [120, 200, 280];
              const radius = layerRadii[layer] + (positionInLayer * 20);
              
              // ارتفاع مختلف لكل عنصر
              const yOffset = (layer - 1) * 40 + (positionInLayer - 1) * 30;
              
              const x = Math.cos(baseAngle) * radius;
              const y = Math.sin(baseAngle) * radius + yOffset;
              
              // حجم وشفافية حسب الطبقة
              const scale = 0.9 + (layer * 0.15);
              const opacity = 0.8 + (layer * 0.1);
              
              return (
                <div
                  key={feature.id}
                  className={`feature-item layer-${layer} ${activeFeature === feature.id ? 'active' : ''}`}
                  style={{
                    transform: `translate(${x}px, ${y}px) scale(${scale})`,
                    opacity,
                    zIndex: 100 + layer * 10 + positionInLayer,
                    ...getParallaxStyle(1.1 * scale, index),
                  }}
                  data-aos="zoom-in"
                  data-aos-delay={index * 150}
                  onMouseEnter={() => handleFeatureHover(feature.id)}
                  onMouseLeave={handleFeatureLeave}
                >
                  <div className="feature-number">{feature.id}</div>
                  <div className="feature-icon" style={{ backgroundColor: feature.color }}>
                    {feature.icon}
                  </div>
                  <div className="feature-content">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                    <div className="feature-details">
                      {feature.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="feature-detail-item">
                          <span className="detail-bullet">•</span>
                          <span className="detail-text">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Curved Connection Lines */}
          <div className="connection-lines">
            {features.map((_, index) => {
              const layer = Math.floor(index / 3);
              const positionInLayer = index % 3;
              const layerAngles = [0, 120, 240];
              const baseAngle = (layerAngles[layer] + positionInLayer * 40) * (Math.PI / 180);
              const layerRadii = [120, 200, 280];
              const radius = layerRadii[layer] + (positionInLayer * 20);
              const yOffset = (layer - 1) * 40 + (positionInLayer - 1) * 30;
              
              const x = Math.cos(baseAngle) * radius;
              const y = Math.sin(baseAngle) * radius + yOffset;
              
              const angleDeg = (baseAngle * 180) / Math.PI;
              const lineLength = radius * 0.8;
              
              return (
                <div
                  key={index}
                  className={`connection-line layer-${layer} ${activeFeature === index + 1 ? 'active' : ''}`}
                  style={{
                    transform: `translate(${x}px, ${y}px) rotate(${angleDeg}deg)`,
                    width: `${lineLength}px`,
                    ...getParallaxStyle(0.5, index)
                  }}
                ></div>
              );
            })}
          </div>
        </div>

        {/* Additional Content Section */}
        <div className="features-extra-content " data-aos="fade-up" data-aos-delay="400">
          <h3 className="features-title" style={{ fontSize: '2.5rem', marginBottom: '30px', marginTop : "70px" }}>
            لماذا تختار منصة علمني؟
          </h3>
          
          <div className="features-highlights">
            <div className="highlight-card" data-aos="fade-up" data-aos-delay="500">
              <span className="highlight-icon">🚀</span>
              <h4 className="highlight-title">تعلم سريع وفعال</h4>
              <p className="highlight-description">
                منهجية تعليمية متطورة تضمن لك التعلم بأقصى سرعة وفعالية، مع تقنيات حديثة تجعل عملية التعلم ممتعة ومثمرة
              </p>
            </div>
            
            <div className="highlight-card" data-aos="fade-up" data-aos-delay="600">
              <span className="highlight-icon">🎯</span>
              <h4 className="highlight-title">نتائج مضمونة</h4>
              <p className="highlight-description">
                ضمان تحقيق أهدافك التعليمية مع نظام متابعة ذكي وتقييم مستمر، لضمان وصولك لأعلى مستويات الأداء
              </p>
            </div>
            
            <div className="highlight-card" data-aos="fade-up" data-aos-delay="700">
              <span className="highlight-icon">💎</span>
              <h4 className="highlight-title">جودة عالية</h4>
              <p className="highlight-description">
                محتوى تعليمي عالي الجودة من خبراء متخصصين، مع تحديثات مستمرة لضمان مواكبة أحدث التطورات
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 